import * as THREE from "three";

/**
 * The ground's slope, baked into a world-space texture so the shader can read it per pixel.
 *
 * Anything a shader keys off the slope — where grass gives way to rock, how much of the side projections the
 * triplanar mix takes — has to vary smoothly across the surface, or its edge shows the triangles. Neither per-face
 * nor per-vertex slope does: the steepest ramps (the mesa's two-unit steps) are narrower than one quad of the ground
 * mesh, so a single row of vertices carries all of the steepness and the rock line zigzags from vertex to vertex
 * along the triangle diagonals. A texture sampled from the height field at several texels per quad has no triangles
 * in it, and bilinear filtering gives a soft edge for free.
 *
 * Channels:
 * - R and G carry the normal's x and z (mapped from -1..1 to 0..1); y follows from them.
 * - B is the CURVATURE of the height field (0.5 flat, above it convex, below it concave). Bench tops and ramp
 *   shoulders are convex, ramp feet and hollows concave; the ground shader dries and lightens the one and darkens and
 *   greens the other, which is the depth cue that makes a bench read as a bench from straight above.
 * - A is baked OCCLUSION, 1 open and 0 buried: horizon AO from the height field (the foot of a ramp is darker than
 *   the top of it) multiplied by soft contact shadows under everything that stands on the ground — blockers, trees,
 *   the fortress, towers. Phones run without GTAO (the `high` tier has `ao:false`), and without this nothing on the
 *   map touches the ground; it is the cheapest possible replacement — one texture tap the shader already makes.
 */
export interface SlopeMap {
  readonly texture: THREE.DataTexture;
  /** World XZ of texel (0, 0)'s centre, and the world size one texture repeat covers. */
  readonly origin: THREE.Vector2;
  readonly size: THREE.Vector2;
  /** Texel counts, and the horizon occlusion alone (0..1) that the contact shadows are multiplied into. */
  readonly w: number;
  readonly h: number;
  readonly horizon: Float32Array;
}

/**
 * Something standing on the ground that should darken it: a disc of radius `r` (or, with `hx`/`hz`, a rectangle of
 * those half-extents, the fortress) at unwarped ground XZ, darkening by up to `s` at its centre.
 */
export interface Contact { readonly x: number; readonly z: number; readonly r: number; readonly s: number; readonly hx?: number; readonly hz?: number }

/**
 * How far the fortress's contact shadow runs past its 3 x 2 footprint (world units, each side): the fortress v2 model
 * overhangs the footprint by ~0.4 at the sides and back, so its seat starts a little outside the cells rather than
 * inside the walls; the falloff `r` does the rest.
 */
export const FORTRESS_SEAT = 0.1;

/**
 * Contact shadows registered by the views that place things on the ground (foliage, the fortress). Views are built
 * in no fixed order relative to the ground — the level builds its props first, the hub its ground first, and the
 * scenery past the map edge arrives after both — so this is a registry the ground re-reads whenever `version` moves
 * (see GroundMesh), rather than a list handed to it once.
 */
export const GroundContacts = {
  list: [] as Contact[],
  version: 0,
  clear(): void { this.list.length = 0; this.version++; },
  add(c: Contact): void { this.list.push(c); this.version++; },
};

/**
 * How steep the ground is at (x, z): 1 − |n.y| of the height field's normal, from central differences `eps` apart —
 * the same measure (and, at eps = 1/TexelsPerUnit, the same differences) as the slope bake the ground shader reads,
 * where grass gives way to rock from about 0.48. 0 is flat; a walkable meadow ramp reaches ~0.45. Pure.
 */
export function slopeAmount(height: (x: number, z: number) => number, x: number, z: number, eps = 1 / 8): number {
  const hx = (height(x + eps, z) - height(x - eps, z)) / (2 * eps);
  const hz = (height(x, z + eps) - height(x, z - eps)) / (2 * eps);
  return 1 - 1 / Math.hypot(hx, 1, hz);
}

/**
 * Where the scattered ground detail (tufts, flowers, weeds, small dressing props) gives out on a slope (M30 #16,
 * "grass/weeds on the slopes"): all of it below `full`, none above `bare`, a thinning hashed share between. Well below
 * the shader's rock line, so a ramp reads as a clean grassy bank and a cliff shoulder as bare, not a hedge of weeds
 * sticking out sideways. `keep` is a per-instance hash in [0, 1).
 */
export const SLOPE_DETAIL = { full: 0.07, bare: 0.17 } as const;
export function detailSurvivesSlope(slope: number, keep: number): boolean {
  if (slope <= SLOPE_DETAIL.full) return true;
  if (slope >= SLOPE_DETAIL.bare) return false;
  return keep >= (slope - SLOPE_DETAIL.full) / (SLOPE_DETAIL.bare - SLOPE_DETAIL.full);
}

/** Texels per world unit: twice the ground mesh's four quads per cell, which is what resolves a sub-quad ramp. */
export const TexelsPerUnit = 8;

/**
 * Horizon AO: for each texel, the highest the height field rises in each of `AoDirs` directions within `AoSteps`,
 * as the sine of its elevation; occlusion is their mean. Four steps out to 1.75 units see a ramp or a bench edge
 * beside the texel, and nothing further — this is contact-scale darkening, not a sky-visibility model.
 */
const AoDirs = 8;
const AoSteps = [2, 4, 8, 14];                     // in texels: 0.25, 0.5, 1 and 1.75 world units
/**
 * Elevation below this sine is ignored, so the micro-relief's gentle swell and the rolling hills do not grey the
 * whole field; only a real rise beside the texel (a ramp, a bench edge) occludes.
 */
const AoBias = 0.1;
/**
 * How strongly the horizon occludes. Tuned on the five terrain profiles: the median texel stays open, a quarter of
 * the map is 3–8% darker (bench feet, hollows) and the darkest 5% about 25% darker (the foot of the mesa's steps).
 */
const AoStrength = 0.75;
/** Half-span of the Laplacian in texels (0.5 units): wide enough to see a bench shoulder, not a single bump. */
const CurvSpan = 4;
/**
 * Scale from curvature (per unit²) to the channel. A 1.2-unit rise over a 1.5-unit ramp peaks near 3 and so
 * saturates; the rolling ground between benches stays within about a quarter of the range.
 */
const CurvScale = 0.2;

/**
 * Samples `height` over the world rectangle [x0, x1] × [z0, z1] (in the ground's UNWARPED coordinates — the ones its
 * uv attribute carries) and returns the normal, curvature and horizon-occlusion map of it. Each height is sampled
 * once; everything else is arithmetic on that array (~10–25 ms for a large level, once per level).
 */
export function bakeSlope(height: (x: number, z: number) => number, x0: number, z0: number, x1: number, z1: number): SlopeMap {
  const w = Math.ceil((x1 - x0) * TexelsPerUnit) + 1, h = Math.ceil((z1 - z0) * TexelsPerUnit) + 1;
  const step = 1 / TexelsPerUnit;
  const ys = new Float32Array(w * h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) ys[j * w + i] = height(x0 + i * step, z0 + j * step);
  const data = new Uint8Array(w * h * 4);
  const at = (i: number, j: number) => ys[Math.min(h - 1, Math.max(0, j)) * w + Math.min(w - 1, Math.max(0, i))];
  const horizon = new Float32Array(w * h);
  // the AO's sample pattern as integer texel offsets, precomputed: this loop runs 32 times per texel
  const offI: number[] = [], offJ: number[] = [], dist2: number[] = [];
  for (let k = 0; k < AoDirs; k++) {
    const a = (k / AoDirs) * Math.PI * 2;
    for (const n of AoSteps) {
      const di = Math.round(Math.cos(a) * n), dj = Math.round(Math.sin(a) * n);
      offI.push(di); offJ.push(dj); dist2.push((di * di + dj * dj) * step * step);
    }
  }
  const S = AoSteps.length, s = CurvSpan, d2 = (CurvSpan * step) ** 2;
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const y0 = ys[j * w + i];
      const hx = (at(i + 1, j) - at(i - 1, j)) / (2 * step);
      const hz = (at(i, j + 1) - at(i, j - 1)) / (2 * step);
      const l = Math.hypot(hx, 1, hz);
      const o = (j * w + i) * 4;
      data[o] = Math.round((-hx / l * 0.5 + 0.5) * 255);
      data[o + 1] = Math.round((-hz / l * 0.5 + 0.5) * 255);
      // curvature: minus the Laplacian, so a hilltop (heights falling away all round) is positive
      const lap = (at(i + s, j) + at(i - s, j) + at(i, j + s) + at(i, j - s) - 4 * y0) / d2;
      data[o + 2] = Math.round((clamp(-lap * CurvScale, -1, 1) * 0.5 + 0.5) * 255);
      let occ = 0;
      for (let k = 0, q = 0; k < AoDirs; k++) {
        let best = 0;
        for (let n = 0; n < S; n++, q++) {
          let ii = i + offI[q], jj = j + offJ[q];
          ii = ii < 0 ? 0 : ii >= w ? w - 1 : ii;
          jj = jj < 0 ? 0 : jj >= h ? h - 1 : jj;
          const dh = ys[jj * w + ii] - y0;
          if (dh <= 0) continue;
          const e = dh / Math.sqrt(dh * dh + dist2[q]) - AoBias;
          if (e > best) best = e;
        }
        occ += best;
      }
      horizon[j * w + i] = clamp(1 - (occ / AoDirs) * AoStrength, 0, 1);
      data[o + 3] = Math.round(horizon[j * w + i] * 255);
    }
  }
  const texture = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.magFilter = texture.minFilter = THREE.LinearFilter;
  texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  // the texel centres sit on the samples, so the texture spans half a texel either side of the sampled rectangle
  return { texture, w, h, horizon, origin: new THREE.Vector2(x0 - step / 2, z0 - step / 2), size: new THREE.Vector2(w * step, h * step) };
}

/**
 * How much a contact darkens the ground `d` units outside its edge (d ≤ 0 is under it).
 *
 * A strong core that fades out over the contact's radius — the shadow a trunk or a tower's plinth throws under the
 * sky — and a faint tail a cell further out. The tail is barely visible as darkening (4%), but the ground shader reads
 * it as "something grows here" and pushes the grass lush around every tree and rock (terrain F1).
 */
export function contactShade(d: number, r: number, s: number): number {
  if (d <= 0) return s;
  const core = d < r ? 1 - smooth(d / r) : 0;
  const tail = d < r + Tail ? 1 - smooth(d / (r + Tail)) : 0;
  return Math.min(1, s * core + TailStrength * tail);
}
/** Reach of the faint lush tail past a contact's core, in world units, and its darkening. */
const Tail = 1.1;
const TailStrength = 0.05;

/**
 * Rewrites the occlusion channel: the horizon AO, times the shade of every contact. Called once when the ground is
 * built and again whenever a tower is built or sold or a view registers more props; the whole channel is a few
 * hundred thousand multiplies, well under a millisecond, and one texture upload.
 */
export function stampContacts(map: SlopeMap, contacts: readonly Contact[]): void {
  const { w, h, horizon } = map;
  const occ = Float32Array.from(horizon);
  const ox = map.origin.x + 0.5 / TexelsPerUnit, oz = map.origin.y + 0.5 / TexelsPerUnit;   // texel (0,0)'s centre
  for (const c of contacts) {
    const hx = c.hx ?? 0, hz = c.hz ?? 0;
    const reach = c.r + Tail;
    const i0 = Math.max(0, Math.floor((c.x - hx - reach - ox) * TexelsPerUnit)), i1 = Math.min(w - 1, Math.ceil((c.x + hx + reach - ox) * TexelsPerUnit));
    const j0 = Math.max(0, Math.floor((c.z - hz - reach - oz) * TexelsPerUnit)), j1 = Math.min(h - 1, Math.ceil((c.z + hz + reach - oz) * TexelsPerUnit));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const px = ox + i / TexelsPerUnit, pz = oz + j / TexelsPerUnit;
        // distance outside the disc (hx = hz = 0) or the rectangle, whose corners are rounded by r
        const d = Math.hypot(Math.max(0, Math.abs(px - c.x) - hx), Math.max(0, Math.abs(pz - c.z) - hz)) - (hx || hz ? 0 : c.r * 0.35);
        const k = contactShade(d, c.r, c.s);
        if (k > 0) occ[j * w + i] *= 1 - k;
      }
    }
  }
  const data = map.texture.image.data as Uint8Array;
  for (let t = 0; t < w * h; t++) data[t * 4 + 3] = Math.round(occ[t] * 255);
  map.texture.needsUpdate = true;
}

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
function smooth(t: number): number { const u = clamp(t, 0, 1); return u * u * (3 - 2 * u); }
